import React, { useState, useEffect, useMemo, useRef } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, MenuItem, FormControl, FormLabel,
    RadioGroup, FormControlLabel, Radio,
    Box, Typography, Select, InputLabel,
    FormHelperText, Card, CardContent, Grid,
    Autocomplete
} from "@mui/material";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import DOMPurify from 'dompurify';
import { getClassWorkoutImage } from "../api/classesApi";
import { searchUsers } from "../api/membersApi";

const CUSTOM_TRAINER_VALUE = "__custom__";

export default function TrainingModal({ open, onClose, onSave, selectedClass, trainers = [] }) {
    const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif", "image/avif"];
    const MAX_UPLOAD_IMAGE_BYTES = 15 * 1024 * 1024;
    const [trainingData, setTrainingData] = useState({
        trainingType: "WOD",
        trainingName: "",
        time: "",
        duration: "",
        trainer: "",
        location: "",
        memberCapacity: "",
        repeatWeekly: false,
        wodName: "",
        wodType: "",
        description: "",
        descriptionIntermediate: "",
        descriptionBeginner: "",
        descriptionStretching: "",
        descriptionAtHome: "",
        canRegister: true,
        freeClass: false,
        videoLinks: [],
    });

    // FIX: Add loading state to prevent double submissions
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [imageError, setImageError] = useState("");
    const [existingWorkoutImage, setExistingWorkoutImage] = useState(null);
    const [selectedWorkoutImageFile, setSelectedWorkoutImageFile] = useState(null);
    const [selectedWorkoutImagePreview, setSelectedWorkoutImagePreview] = useState("");
    const [removeWorkoutImage, setRemoveWorkoutImage] = useState(false);
    const workoutImageInputRef = useRef(null);
    const [trainerSelect, setTrainerSelect] = useState("");
    const [temporaryTrainer, setTemporaryTrainer] = useState(null);
    const [userSearchResults, setUserSearchResults] = useState([]);
    const [isSearchingUsers, setIsSearchingUsers] = useState(false);
    const selectMenuProps = {
        disablePortal: false,
        PaperProps: {
            sx: {
                zIndex: 2305
            }
        },
        sx: {
            zIndex: 2305
        }
    };

    // Add errors state to track validation errors
    const [errors, setErrors] = useState({
        trainingType: false,
        trainingName: false,
        time: false,
        duration: false,
        memberCapacity: false,
        temporaryTrainer: false
    });
    const [videoLinkErrors, setVideoLinkErrors] = useState([]);

    const quillModules = useMemo(() => ({
        toolbar: [
            ["bold", "italic"],
            [{ color: ["#c62929", "#1a7e1a", "#3434d6", "#a1a166", "#ffa500", "#800080", false] }],
            ["clean"]
        ]
    }), []);

    const quillFormats = useMemo(() => ["bold", "italic", "color"], []);
    const parseVideoLinks = (value) => {
        if (!value) return [];
        if (Array.isArray(value)) {
            return value.filter((link) => typeof link === "string").map((link) => link.trim()).filter(Boolean);
        }
        if (typeof value === "string") {
            try {
                const parsed = JSON.parse(value);
                if (Array.isArray(parsed)) {
                    return parsed.filter((link) => typeof link === "string").map((link) => link.trim()).filter(Boolean);
                }
            } catch (e) {
                return [];
            }
        }
        return [];
    };

    const isValidYouTubeLink = (url) => {
        if (!url || typeof url !== "string") return false;
        try {
            const parsed = new URL(url.trim());
            const host = parsed.hostname.replace("www.", "");
            if (host === "youtu.be") {
                return parsed.pathname.length > 1;
            }
            if (host === "youtube.com" || host === "m.youtube.com") {
                if (parsed.pathname === "/watch") {
                    return !!parsed.searchParams.get("v");
                }
                return parsed.pathname.startsWith("/shorts/") || parsed.pathname.startsWith("/embed/");
            }
            return false;
        } catch (error) {
            return false;
        }
    };

    const toEditorHtml = (value) => {
        if (!value) return "";
        const hasHtml = /<\/?[a-z][\s\S]*>/i.test(value);
        if (!hasHtml) {
            return value
                .replace(/\r\n/g, "\n")
                .replace(/\r/g, "\n")
                .replace(/\n/g, "<br>");
        }
        let html = DOMPurify.sanitize(value, {
            ALLOWED_TAGS: ["p", "br", "strong", "em", "b", "i", "span"],
            ALLOWED_ATTR: ["style"],
            ALLOWED_STYLES: {
                "*": {
                    color: [/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, /^rgb\((\s*\d+\s*,){2}\s*\d+\s*\)$/]
                }
            },
            KEEP_CONTENT: true
        });
        html = html
            .replace(/<b(\s|>)/gi, "<strong$1")
            .replace(/<\/b>/gi, "</strong>")
            .replace(/<i(\s|>)/gi, "<em$1")
            .replace(/<\/i>/gi, "</em>")
            .replace(/^\s*<br\s*\/?>/gi, "")
            .replace(/<br\s*\/?>\s*$/gi, "");
        const hasBlock = /<(p|div|h[1-6]|ul|ol|li|blockquote|pre)\b/i.test(html);
        if (!hasBlock && html.trim()) {
            html = `<p>${html}</p>`;
        }
        return html;
    };

    const cleanQuillHTML = (html) => {
        if (!html) return "";
        return html
            .replace(/>\s*\n\s*</g, "><")
            .replace(/(<p><br><\/p>){4,}/gi, "<p><br></p><p><br></p><p><br></p>")
            .replace(/^(<p><br><\/p>)+/gi, "")
            .replace(/(<p><br><\/p>)+$/gi, "")
            .trim();
    };

    const getEditorSx = (hasError = false) => ({
        '& .ql-toolbar': {
            borderColor: hasError ? '#d32f2f' : '#AAAAAA',
            borderTopLeftRadius: '4px',
            borderTopRightRadius: '4px'
        },
        '& .ql-container': {
            minHeight: '120px',
            borderColor: hasError ? '#d32f2f' : '#AAAAAA',
            borderBottomLeftRadius: '4px',
            borderBottomRightRadius: '4px',
            fontSize: '14px'
        },
        '& .ql-editor': {
            minHeight: '120px',
            lineHeight: '1.35',
            paddingTop: '8px',
            paddingBottom: '8px'
        }
    });

    useEffect(() => {
        if (selectedClass) {
            const hasTemporaryTrainer = !!selectedClass.temporaryTrainerId;
            setTrainingData({
                id: selectedClass.id,
                trainingType: selectedClass.trainingType || "WOD",
                trainingName: selectedClass.trainingName || "",
                time: selectedClass.time || "",
                duration: selectedClass.duration || 0,
                trainer: selectedClass.trainer || "",
                location: selectedClass.location || "",
                memberCapacity: selectedClass.memberCapacity || "",
                repeatWeekly: selectedClass.repeatWeekly || false,
                wodName: selectedClass.wodName || "",
                wodType: selectedClass.wodType || "",
                description: toEditorHtml(selectedClass.description || ""),
                descriptionIntermediate: toEditorHtml(selectedClass.descriptionIntermediate || ""),
                descriptionBeginner: toEditorHtml(selectedClass.descriptionBeginner || ""),
                descriptionStretching: toEditorHtml(selectedClass.descriptionStretching || ""),
                descriptionAtHome: toEditorHtml(selectedClass.descriptionAtHome || ""),
                canRegister: selectedClass.canRegister ?? true,
                freeClass: selectedClass.freeClass || false,
                applyToAllFutureTrainings: selectedClass.applyToAllFutureTrainings || false,
                videoLinks: parseVideoLinks(selectedClass.videoLinks),
            });
            if (hasTemporaryTrainer) {
                setTrainerSelect(CUSTOM_TRAINER_VALUE);
                setTemporaryTrainer({
                    id: selectedClass.temporaryTrainerId,
                    fullName: selectedClass.temporaryTrainerName || selectedClass.trainer || "Unknown",
                    email: ""
                });
                setUserSearchResults([{
                    id: selectedClass.temporaryTrainerId,
                    fullName: selectedClass.temporaryTrainerName || selectedClass.trainer || "Unknown",
                    email: ""
                }]);
            } else {
                setTrainerSelect(selectedClass.trainer || "");
                setTemporaryTrainer(null);
                setUserSearchResults([]);
            }
            // Reset errors when loading existing class
            setErrors({
                trainingType: false,
                trainingName: false,
                time: false,
                duration: false,
                memberCapacity: false,
                temporaryTrainer: false
            });
        } else {
            setTrainingData({
                trainingType: "WOD",
                trainingName: "",
                time: "",
                duration: "",
                trainer: "",
                location: "",
                memberCapacity: "",
                repeatWeekly: false,
                wodName: "",
                wodType: "NONE",
                description: "",
                descriptionIntermediate: "",
                descriptionBeginner: "",
                descriptionStretching: "",
                descriptionAtHome: "",
                canRegister: true,
                freeClass: false,
                applyToAllFutureTrainings: false,
                videoLinks: [],
            });
            setTrainerSelect("");
            setTemporaryTrainer(null);
            setUserSearchResults([]);
        }
        // FIX: Reset submitting state when modal reopens
        setIsSubmitting(false);
        setImageError("");
        setSelectedWorkoutImageFile(null);
        setSelectedWorkoutImagePreview("");
        setRemoveWorkoutImage(false);
        setExistingWorkoutImage(null);
        setVideoLinkErrors([]);
        setIsSearchingUsers(false);
    }, [selectedClass]);

    useEffect(() => {
        let isCancelled = false;
        const loadWorkoutImage = async () => {
            if (!open || !selectedClass?.id) return;
            const imageData = await getClassWorkoutImage(selectedClass.id);
            if (!isCancelled && imageData?.imageData) {
                setExistingWorkoutImage(imageData.imageData);
            }
        };
        loadWorkoutImage();
        return () => {
            isCancelled = true;
        };
    }, [open, selectedClass?.id]);

    useEffect(() => () => {
        if (selectedWorkoutImagePreview) {
            URL.revokeObjectURL(selectedWorkoutImagePreview);
        }
    }, [selectedWorkoutImagePreview]);

    // Dropdowni valikud
    const trainingTypes = ["WOD", "Weightlifting", "Cardio", "Rowing", "Gymnastics", "Open Gym", "Kids", "Basic", "Hyrox", "Circuit", "Strength", "Hybrid", "Mobility", "Canoeing", "Other"];
    const wodTypes = ["For Time", "EMOM", "AMRAP", "TABATA", "For load", "For Quality", "NONE"];

    const formatDateForInput = (isoString) => {
        if (!isoString) return "";
        const date = new Date(isoString);

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");

        return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setTrainingData({ ...trainingData, [name]: value });

        // Clear error for this field when user types something
        if (errors[name]) {
            setErrors({ ...errors, [name]: false });
        }
    };

    const handleTrainerSelectChange = (e) => {
        const value = e.target.value;
        setTrainerSelect(value);

        if (value === CUSTOM_TRAINER_VALUE) {
            setTrainingData((prev) => ({
                ...prev,
                trainer: temporaryTrainer?.fullName || ""
            }));
            return;
        }

        setTemporaryTrainer(null);
        setUserSearchResults([]);
        setTrainingData((prev) => ({ ...prev, trainer: value }));
        if (errors.temporaryTrainer) {
            setErrors((prev) => ({ ...prev, temporaryTrainer: false }));
        }
    };

    const handleSearchUsers = async (searchTerm) => {
        if (!searchTerm || searchTerm.trim().length < 2) {
            setUserSearchResults(temporaryTrainer ? [temporaryTrainer] : []);
            return;
        }

        setIsSearchingUsers(true);
        try {
            const results = await searchUsers(searchTerm.trim());
            const list = Array.isArray(results) ? results : [];
            if (temporaryTrainer && !list.some((user) => user.id === temporaryTrainer.id)) {
                setUserSearchResults([temporaryTrainer, ...list]);
            } else {
                setUserSearchResults(list);
            }
        } catch (error) {
            console.error("Error searching users:", error);
            setUserSearchResults(temporaryTrainer ? [temporaryTrainer] : []);
        } finally {
            setIsSearchingUsers(false);
        }
    };

    const handleRadioChange = (e) => {
        setTrainingData({ ...trainingData, [e.target.name]: e.target.value });
    };

    const handleDescriptionInput = (value) => setTrainingData(prev => ({ ...prev, description: value }));
    const handleIntermediateInput = (value) => setTrainingData(prev => ({ ...prev, descriptionIntermediate: value }));
    const handleBeginnerInput = (value) => setTrainingData(prev => ({ ...prev, descriptionBeginner: value }));
    const handleStretchingInput = (value) => setTrainingData(prev => ({ ...prev, descriptionStretching: value }));
    const handleAtHomeInput = (value) => setTrainingData(prev => ({ ...prev, descriptionAtHome: value }));
    const handleVideoLinkChange = (index, value) => {
        setTrainingData((prev) => {
            const links = Array.isArray(prev.videoLinks) ? [...prev.videoLinks] : [];
            links[index] = value;
            return { ...prev, videoLinks: links };
        });
        setVideoLinkErrors((prev) => {
            const next = [...prev];
            next[index] = "";
            return next;
        });
    };

    const handleAddVideoLink = () => {
        setTrainingData((prev) => ({
            ...prev,
            videoLinks: [...(Array.isArray(prev.videoLinks) ? prev.videoLinks : []), ""]
        }));
        setVideoLinkErrors((prev) => [...prev, ""]);
    };

    const handleRemoveVideoLink = (index) => {
        setTrainingData((prev) => {
            const links = Array.isArray(prev.videoLinks) ? [...prev.videoLinks] : [];
            links.splice(index, 1);
            return { ...prev, videoLinks: links };
        });
        setVideoLinkErrors((prev) => {
            const next = [...prev];
            next.splice(index, 1);
            return next;
        });
    };

    const compressImageInBrowser = (file) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const img = new Image();
            img.onload = () => {
                const maxDim = 1600;
                const ratio = Math.min(maxDim / img.width, maxDim / img.height, 1);
                const width = Math.round(img.width * ratio);
                const height = Math.round(img.height * ratio);
                const canvas = document.createElement("canvas");
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext("2d");
                if (!ctx) return reject(new Error("Failed to prepare image compression"));
                ctx.drawImage(img, 0, 0, width, height);

                canvas.toBlob(
                    (blob) => {
                        if (!blob) return reject(new Error("Failed to compress image"));
                        const compressedFile = new File([blob], file.name.replace(/\.[^.]+$/, ".jpg"), { type: "image/jpeg" });
                        resolve(compressedFile);
                    },
                    "image/jpeg",
                    0.82
                );
            };
            img.onerror = () => reject(new Error("Invalid image file"));
            img.src = String(reader.result);
        };
        reader.onerror = () => reject(new Error("Failed to read image file"));
        reader.readAsDataURL(file);
    });

    const handleWorkoutImageChange = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
            setImageError("Only JPG, PNG, WEBP, GIF and AVIF image formats are allowed.");
            return;
        }

        if (file.size > MAX_UPLOAD_IMAGE_BYTES) {
            setImageError("Image is too large. Maximum file size is 15MB.");
            return;
        }

        setImageError("");

        try {
            const compressedFile = await compressImageInBrowser(file);
            const objectUrl = URL.createObjectURL(compressedFile);
            setSelectedWorkoutImageFile(compressedFile);
            setSelectedWorkoutImagePreview(objectUrl);
            setRemoveWorkoutImage(false);
        } catch (error) {
            setImageError(error.message || "Failed to process selected image.");
        }
    };

    const handleRemoveWorkoutImage = () => {
        if (selectedWorkoutImagePreview) {
            URL.revokeObjectURL(selectedWorkoutImagePreview);
        }
        setSelectedWorkoutImageFile(null);
        setSelectedWorkoutImagePreview("");
        setRemoveWorkoutImage(true);
        setImageError("");
    };

    // Function to validate the form
    const validateForm = () => {
        const newErrors = {
            trainingType: !trainingData.trainingType,
            trainingName: !trainingData.trainingName,
            time: !trainingData.time,
            duration: !trainingData.duration,
            memberCapacity: !trainingData.memberCapacity,
            temporaryTrainer: trainerSelect === CUSTOM_TRAINER_VALUE && !temporaryTrainer?.id
        };

        setErrors(newErrors);

        const links = Array.isArray(trainingData.videoLinks) ? trainingData.videoLinks : [];
        const normalizedLinks = links.map((link) => (typeof link === "string" ? link.trim() : ""));
        const linkErrors = normalizedLinks.map((link) => {
            if (!link) return "";
            return isValidYouTubeLink(link) ? "" : "Please enter a valid YouTube link";
        });
        setVideoLinkErrors(linkErrors);
        const hasVideoLinkErrors = linkErrors.some(Boolean);

        // Form is valid if no errors (all required fields are filled)
        return !Object.values(newErrors).some(error => error) && !hasVideoLinkErrors;
    };

    const handleSubmit = async () => {
        // FIX: Prevent double submissions
        if (isSubmitting) {
            console.log("⏳ Already submitting, please wait...");
            return;
        }

        // Validate the form before submission
        const isValid = validateForm();
        if (imageError) {
            return;
        }

        if (isValid) {
            if (!trainingData || typeof trainingData !== "object") {
                console.error("❌ Invalid training data before submission:", trainingData);
                return;
            }

            // Set submitting state
            setIsSubmitting(true);

            try {
                const sanitizeForSave = (html) => DOMPurify.sanitize(cleanQuillHTML(html || ""), {
                    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'b', 'i', 'span'],
                    ALLOWED_ATTR: ['style'],
                    ALLOWED_STYLES: {
                        '*': {
                            color: [/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, /^rgb\((\s*\d+\s*,){2}\s*\d+\s*\)$/]
                        }
                    },
                    KEEP_CONTENT: true
                });

                const isCustomTrainer = trainerSelect === CUSTOM_TRAINER_VALUE;
                await onSave({
                    ...trainingData,
                    trainer: isCustomTrainer
                        ? (temporaryTrainer?.fullName || trainingData.trainer || "")
                        : (trainerSelect || ""),
                    temporaryTrainerId: isCustomTrainer ? (temporaryTrainer?.id || null) : null,
                    description: sanitizeForSave(trainingData.description),
                    descriptionIntermediate: sanitizeForSave(trainingData.descriptionIntermediate),
                    descriptionBeginner: sanitizeForSave(trainingData.descriptionBeginner),
                    descriptionStretching: sanitizeForSave(trainingData.descriptionStretching),
                    descriptionAtHome: sanitizeForSave(trainingData.descriptionAtHome),
                    videoLinks: (trainingData.videoLinks || []).map((link) => link.trim()).filter(Boolean),
                    workoutImageFile: selectedWorkoutImageFile,
                    removeWorkoutImage
                });
            } finally {
                // Always reset submitting state
                setIsSubmitting(false);
            }
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth slotProps={{ root: { sx: { zIndex: 2100 } } }}>
            <DialogTitle>{selectedClass ? "Edit Training" : "Add New Training"}</DialogTitle>
            <DialogContent sx={{pt: 1, mt: 1}}>
                {/* Basic Information */}
                <Card variant="outlined" sx={{ mb: 3, mt: 1 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                            Basic Information
                        </Typography>

                        <TextField
                            select
                            fullWidth
                            label="Training Type*"
                            name="trainingType"
                            value={trainingData.trainingType}
                            onChange={handleChange}
                            margin="normal"
                            error={errors.trainingType}
                            helperText={errors.trainingType ? "Training type is required" : ""}
                            SelectProps={{ MenuProps: selectMenuProps }}
                        >
                            {trainingTypes.map((type) => (
                                <MenuItem key={type} value={type}>
                                    {type}
                                </MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            fullWidth
                            label="Training Name*"
                            name="trainingName"
                            value={trainingData.trainingName}
                            onChange={handleChange}
                            margin="normal"
                            error={errors.trainingName}
                            helperText={errors.trainingName ? "Training name is required" : ""}
                        />

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Time*"
                                    name="time"
                                    type="datetime-local"
                                    value={formatDateForInput(trainingData.time)}
                                    onChange={handleChange}
                                    margin="normal"
                                    InputLabelProps={{ shrink: true }}
                                    error={errors.time}
                                    helperText={errors.time ? "Time is required" : ""}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Duration (minutes)*"
                                    name="duration"
                                    type="number"
                                    value={trainingData.duration}
                                    onChange={handleChange}
                                    margin="normal"
                                    error={errors.duration}
                                    helperText={errors.duration ? "Duration is required" : ""}
                                />
                            </Grid>
                        </Grid>

                        <TextField
                            select
                            fullWidth
                            label="Trainer"
                            name="trainer"
                            value={trainerSelect}
                            onChange={handleTrainerSelectChange}
                            margin="normal"
                            SelectProps={{ MenuProps: selectMenuProps }}
                            helperText="Custom lets you assign a user who can only view participants for this class."
                        >
                            <MenuItem value="">
                                None
                            </MenuItem>
                            {trainers.map((trainer) => (
                                <MenuItem
                                    key={trainer.trainerId || trainer.fullName}
                                    value={trainer.fullName}
                                >
                                    {trainer.fullName}
                                </MenuItem>
                            ))}
                            <MenuItem value={CUSTOM_TRAINER_VALUE}>
                                Custom
                            </MenuItem>
                        </TextField>

                        {trainerSelect === CUSTOM_TRAINER_VALUE && (
                            <Autocomplete
                                options={userSearchResults}
                                loading={isSearchingUsers}
                                value={temporaryTrainer}
                                getOptionLabel={(option) =>
                                    option?.fullName
                                        ? `${option.fullName}${option.email ? ` (${option.email})` : ""}`
                                        : ""
                                }
                                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                onChange={(e, value) => {
                                    setTemporaryTrainer(value);
                                    setTrainingData((prev) => ({
                                        ...prev,
                                        trainer: value?.fullName || ""
                                    }));
                                    if (errors.temporaryTrainer) {
                                        setErrors((prev) => ({ ...prev, temporaryTrainer: false }));
                                    }
                                }}
                                onInputChange={(e, value, reason) => {
                                    if (reason === "input") {
                                        handleSearchUsers(value);
                                    }
                                }}
                                slotProps={{
                                    popper: {
                                        sx: { zIndex: 2400 }
                                    }
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Search temporary trainer"
                                        placeholder="Type at least 2 characters to search"
                                        margin="normal"
                                        error={errors.temporaryTrainer}
                                        helperText={
                                            errors.temporaryTrainer
                                                ? "Please select a user for Custom trainer"
                                                : "This user can only see participants for this class"
                                        }
                                    />
                                )}
                                noOptionsText="No users found"
                                fullWidth
                            />
                        )}

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Location"
                                    name="location"
                                    value={trainingData.location}
                                    onChange={handleChange}
                                    margin="normal"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Member Capacity*"
                                    name="memberCapacity"
                                    type="number"
                                    value={trainingData.memberCapacity}
                                    onChange={handleChange}
                                    margin="normal"
                                    error={errors.memberCapacity}
                                    helperText={errors.memberCapacity ? "Member capacity is required" : ""}
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                {/* Scheduling Options */}
                <Card variant="outlined" sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                            Scheduling Options
                        </Typography>

                        <FormControl component="fieldset" margin="normal" fullWidth>
                            <FormLabel component="legend">Repeat Weekly?</FormLabel>
                            <FormHelperText sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 1 }}>
                                Yes: Creates recurring weekly classes. No: When updating, removes future recurring classes.
                            </FormHelperText>
                            <RadioGroup
                                row
                                name="repeatWeekly"
                                value={trainingData.repeatWeekly.toString()}
                                onChange={(e) => setTrainingData({ ...trainingData, repeatWeekly: e.target.value === "true" })}
                            >
                                <FormControlLabel value="true" control={<Radio />} label="Yes" />
                                <FormControlLabel value="false" control={<Radio />} label="No" />
                            </RadioGroup>
                        </FormControl>

                        <FormControl component="fieldset" margin="normal" fullWidth>
                            <FormLabel component="legend">Apply to all future trainings?</FormLabel>
                            <FormHelperText sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 1 }}>
                                Yes: Saves these changes to all future classes in the series.
                            </FormHelperText>
                            <RadioGroup
                                row
                                name="applyToAllFutureTraining"
                                value={trainingData.applyToAllFutureTrainings ? "true" : "false"}
                                onChange={(e) => setTrainingData({ ...trainingData, applyToAllFutureTrainings: e.target.value === "true" })}
                            >
                                <FormControlLabel value="true" control={<Radio />} label="Yes" />
                                <FormControlLabel value="false" control={<Radio />} label="No" />
                            </RadioGroup>
                        </FormControl>
                    </CardContent>
                </Card>

                {/* Registration Settings */}
                <Card variant="outlined" sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                            Registration Settings
                        </Typography>

                        <FormControl component="fieldset" margin="normal" fullWidth>
                            <FormLabel component="legend">Can user register for class?</FormLabel>
                            <FormHelperText sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 1 }}>
                                Yes: Users can register for this class. No: Class is visible but registration is disabled.
                            </FormHelperText>
                            <RadioGroup
                                row
                                name="canRegister"
                                value={trainingData.canRegister.toString()}
                                onChange={(e) => setTrainingData({ ...trainingData, canRegister: e.target.value === "true" })}
                            >
                                <FormControlLabel value="true" control={<Radio />} label="Yes" />
                                <FormControlLabel value="false" control={<Radio />} label="No" />
                            </RadioGroup>
                        </FormControl>

                        <FormControl component="fieldset" margin="normal" fullWidth>
                            <FormLabel component="legend">Is this class free?</FormLabel>
                            <FormHelperText sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 1 }}>
                                Yes: Users can attend without using a session from their plan. No: Session will be deducted from user's plan.
                            </FormHelperText>
                            <RadioGroup
                                row
                                name="freeClass"
                                value={trainingData.freeClass.toString()}
                                onChange={(e) => setTrainingData({ ...trainingData, freeClass: e.target.value === "true" })}
                            >
                                <FormControlLabel value="true" control={<Radio />} label="Yes" />
                                <FormControlLabel value="false" control={<Radio />} label="No" />
                            </RadioGroup>
                        </FormControl>
                    </CardContent>
                </Card>

                {/* WOD Information */}
                <Card variant="outlined" sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                            WOD Information
                        </Typography>

                        <FormControl component="fieldset" margin="normal" fullWidth>
                            <FormLabel component="legend">WOD Type</FormLabel>
                            <RadioGroup
                                row
                                name="wodType"
                                value={trainingData.wodType}
                                onChange={handleRadioChange}
                            >
                                {wodTypes.map((type) => (
                                    <FormControlLabel key={type} value={type} control={<Radio />} label={type} />
                                ))}
                            </RadioGroup>
                        </FormControl>

                        <TextField
                            fullWidth
                            label="WOD Name"
                            name="wodName"
                            value={trainingData.wodName}
                            onChange={handleChange}
                            margin="normal"
                        />
                    </CardContent>
                </Card>

                {/* Description */}
                <Card variant="outlined">
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                            Description (RX)
                        </Typography>

                        <Box sx={getEditorSx(false)}>
                            <ReactQuill
                                theme="snow"
                                value={trainingData.description}
                                onChange={handleDescriptionInput}
                                modules={quillModules}
                                formats={quillFormats}
                                placeholder="Description (RX)"
                            />
                        </Box>

                        <Grid container spacing={2} sx={{ mt: 2 }}>
                            <Grid item xs={12}>
                                <Typography variant="subtitle1" sx={{ mb: 1 }}>Intermediate (INT)</Typography>
                                <Box sx={getEditorSx(false)}>
                                    <ReactQuill
                                        theme="snow"
                                        value={trainingData.descriptionIntermediate}
                                        onChange={handleIntermediateInput}
                                        modules={quillModules}
                                        formats={quillFormats}
                                        placeholder="Intermediate"
                                    />
                                </Box>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="subtitle1" sx={{ mb: 1 }}>Beginner (BEG)</Typography>
                                <Box sx={getEditorSx(false)}>
                                    <ReactQuill
                                        theme="snow"
                                        value={trainingData.descriptionBeginner}
                                        onChange={handleBeginnerInput}
                                        modules={quillModules}
                                        formats={quillFormats}
                                        placeholder="Beginner"
                                    />
                                </Box>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="subtitle1" sx={{ mb: 1 }}>Stretching</Typography>
                                <Box sx={getEditorSx(false)}>
                                    <ReactQuill
                                        theme="snow"
                                        value={trainingData.descriptionStretching}
                                        onChange={handleStretchingInput}
                                        modules={quillModules}
                                        formats={quillFormats}
                                        placeholder="Stretching"
                                    />
                                </Box>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="subtitle1" sx={{ mb: 1 }}>At Home</Typography>
                                <Box sx={getEditorSx(false)}>
                                    <ReactQuill
                                        theme="snow"
                                        value={trainingData.descriptionAtHome}
                                        onChange={handleAtHomeInput}
                                        modules={quillModules}
                                        formats={quillFormats}
                                        placeholder="At Home"
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                <Card variant="outlined" sx={{ mt: 3 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                            Workout image
                        </Typography>

                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Supported formats: JPG, PNG, WEBP, GIF, AVIF. Max file size: 15MB.
                        </Typography>

                        {(selectedWorkoutImagePreview || (existingWorkoutImage && !removeWorkoutImage)) && (
                            <Box sx={{ mb: 2 }}>
                                <Box
                                    component="img"
                                    src={selectedWorkoutImagePreview || existingWorkoutImage}
                                    alt="Workout preview"
                                    sx={{
                                        width: "100%",
                                        maxHeight: 260,
                                        objectFit: "cover",
                                        borderRadius: 1,
                                        border: "1px solid #ddd"
                                    }}
                                />
                            </Box>
                        )}

                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                            <Button
                                variant="outlined"
                                onClick={() => workoutImageInputRef.current?.click()}
                            >
                                Select image
                            </Button>
                            {(selectedWorkoutImagePreview || existingWorkoutImage) && (
                                <Button variant="outlined" color="error" onClick={handleRemoveWorkoutImage}>
                                    Remove image
                                </Button>
                            )}
                        </Box>
                        <input
                            ref={workoutImageInputRef}
                            style={{ display: "none" }}
                            accept="image/jpeg,image/png,image/webp,image/gif,image/avif"
                            type="file"
                            onChange={handleWorkoutImageChange}
                        />

                        {imageError && (
                            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                                {imageError}
                            </Typography>
                        )}
                    </CardContent>
                </Card>

                <Card variant="outlined" sx={{ mt: 3 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                            Videos (YouTube)
                        </Typography>

                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Add one or more YouTube links for this class.
                        </Typography>

                        {(trainingData.videoLinks || []).map((link, index) => (
                            <Box key={`video-link-${index}`} sx={{ display: "flex", gap: 1, mb: 1.5 }}>
                                <TextField
                                    fullWidth
                                    label={`YouTube link ${index + 1}`}
                                    value={link}
                                    onChange={(e) => handleVideoLinkChange(index, e.target.value)}
                                    placeholder="https://www.youtube.com/watch?v=..."
                                    error={!!videoLinkErrors[index]}
                                    helperText={videoLinkErrors[index] || ""}
                                />
                                <Button
                                    variant="outlined"
                                    color="error"
                                    onClick={() => handleRemoveVideoLink(index)}
                                >
                                    Remove
                                </Button>
                            </Box>
                        ))}

                        <Button variant="outlined" onClick={handleAddVideoLink}>
                            Add Video
                        </Button>
                    </CardContent>
                </Card>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} color="error" disabled={isSubmitting}>Cancel</Button>
                <Button 
                    onClick={handleSubmit} 
                    variant="contained" 
                    color="primary"
                    disabled={isSubmitting}
                >
                    {isSubmitting ? 'Saving...' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}